/**
 * Human labels for scorecard metric keys — shared by the Session-metric
 * scorecard and the tenant Settings → Metrics visibility tab so both
 * render a key the same way.
 */

/** Keys whose display label differs from the generic title-cased fallback.
 *  `engagement` is shown platform-wide as "Emotional Engagement". */
const METRIC_LABEL: Record<string, string> = {
  engagement: 'Emotional Engagement',
  // Drift-robust motion metrics (SENSOR-DRIFT-ROBUST-METRICS) — the generic
  // fallback title-cases poorly (e.g. bidex_v2 → "Bidex"), so name them.
  left_angular_path: 'Left Angular Path',
  right_angular_path: 'Right Angular Path',
  total_angular_path: 'Angular Path Length',
  left_smoothness_ldlj: 'Left Smoothness (LDLJ)',
  right_smoothness_ldlj: 'Right Smoothness (LDLJ)',
  left_tremor_power: 'Left Tremor Power',
  right_tremor_power: 'Right Tremor Power',
  bidex_v2: 'Bimanual Coactivity (v2)',
  active_duration_fraction: 'Active Duration',
};

/** Human label for a metric key: an explicit override, else the key
 *  with a trailing `_vN` dropped, underscores spaced, and title-cased. */
export function prettyMetric(key: string): string {
  const override = METRIC_LABEL[key];
  if (override) return override;
  const s = key
    .replace(/_v\d+$/, '')
    .replace(/_/g, ' ')
    .trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * One-line, clinician-facing definitions for scorecard metrics — surfaced
 * as the hover tooltip on each metric card. Keyed by the *base* metric
 * (a `_vN` suffix and a leading `left_` / `right_` hand prefix are
 * stripped before lookup), so `left_path_length`, `right_path_length` and
 * `total_path_length` all share one definition. Returns `null` when a key
 * has no description yet — callers fall back to just the label.
 */
const METRIC_DESCRIPTION: Record<string, string> = {
  // Motion (IMU)
  duration: 'Total time to complete the task (seconds). Shorter is better once accuracy is held constant.',
  idle_time: 'Time spent not actively moving during the task. Less idle time reflects a more fluent workflow.',
  active_duration_fraction:
    'Share of the task spent actively moving the instruments, rather than paused or idle.',
  bimanual_dexterity:
    'How closely the two hands’ speeds move together. Retired: built on hand speed, which the wrist sensor can’t measure reliably — see Bimanual coactivity.',
  bidex: 'Bimanual coactivity: how much the two hands are engaged at the same time.',
  path_length:
    'Distance the hand travelled, estimated from the wrist sensor. Not reliable: the sensor can’t measure distance accurately, so treat it as a rough relative figure — use Angular path for economy of motion.',
  angular_path:
    'Cumulative rotation of the hand — a drift-robust proxy for economy of motion (less rotation is more efficient).',
  working_volume:
    'The 3-D volume the hand moved within. A tighter working volume reflects more controlled, contained motion.',
  suture_throws: 'Number of completed suture throws during the task.',
  smoothness_ldlj:
    'Motion smoothness (log dimensionless jerk) — smoother, less jerky movement scores higher.',
  smoothness: 'How smooth the hand motion is — smoother movement scores higher.',
  tremor_power: 'Amount of tremor in the hand motion. Lower tremor is better.',
  tremor: 'Amount of hand tremor. Lower is better.',
  angular_velocity: 'Rotational speed of the hand during the task.',
  mean_speed:
    'Average hand speed, estimated from the wrist sensor. Not reliable (the sensor can’t measure speed accurately) — a rough relative figure only.',
  max_speed:
    'Peak hand speed, estimated from the wrist sensor. Not reliable (the sensor can’t measure speed accurately) — a rough relative figure only.',
  // Cognitive (EEG)
  focus: 'EEG-derived attention: how sustained and directed the focus is during the task.',
  engagement:
    'EEG-derived emotional engagement — how invested and aroused the participant is in the task.',
  workload: 'EEG-derived cognitive workload — how much mental effort the task is demanding.',
  valence: 'EEG-derived emotional valence — positive vs. negative affect during the task.',
  stress: 'EEG-derived stress index. Lower is better.',
  fatigue: 'EEG-derived mental fatigue. Lower is better.',
};

/** Definition for a metric key, or `null` when none is catalogued. Strips a
 *  `_vN` suffix and a `left_`/`right_` hand prefix so hand-specific and
 *  versioned variants share one definition. */
export function metricDescription(key: string): string | null {
  const base = key.replace(/_v\d+$/, '');
  return (
    METRIC_DESCRIPTION[base] ??
    METRIC_DESCRIPTION[base.replace(/^(left|right|total)_/, '')] ??
    null
  );
}
